import * as React from "react";
import { Box, Card, CardContent, Chip, Fade, Grid, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockIcon from "@mui/icons-material/Lock";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { BILL_CARDS as ALL_BILL_CARDS, AUTO_FILL_TYPES } from "./constants";

// Per-card presentation for each state the billing cycle can be in. Keeping it
// as data (rather than nested ternaries in the JSX) means a card can never end
// up in a half-styled state that doesn't match its label.
const STATE_STYLES = {
  ready: { color: "#6366F1", Icon: CloudUploadIcon, chip: null },
  uploaded: {
    color: "#F59E0B",
    Icon: EditIcon,
    chip: { label: "Uploaded", bg: "#FEF3C7", fg: "#B45309" },
  },
  approved: {
    color: "#10B981",
    Icon: CheckCircleIcon,
    chip: { label: "Approved", bg: "#D1FAE5", fg: "#047857" },
  },
  rejected: {
    color: "#DC2626",
    Icon: ErrorOutlineIcon,
    chip: { label: "Rejected", bg: "#FEE2E2", fg: "#B91C1C" },
  },
  locked_cycle: {
    color: "#94A3B8",
    Icon: ScheduleIcon,
    chip: { label: "Not yet", bg: "#F1F5F9", fg: "#64748B" },
  },
  skipped: {
    color: "#94A3B8",
    Icon: TaskAltIcon,
    chip: { label: "Settled", bg: "#F1F5F9", fg: "#64748B" },
  },
};

export default function BillCards({
  cards,
  isTenantUploader,
  onCardClick,
  statusFor,
}) {
  const visibleCards = cards || ALL_BILL_CARDS;

  return (
    <Grid container spacing={3}>
      {visibleCards.map((card, index) => {
        const status = statusFor?.(card.type) || { state: "ready" };
        const style = STATE_STYLES[status.state] || STATE_STYLES.ready;
        const isDisabled = Boolean(status.disabled) && !isTenantUploader;
        const StateIcon = style.Icon;

        return (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.type}>
            <Fade in timeout={400 + index * 100}>
              <Card
                onClick={() => {
                  if (!isDisabled) onCardClick(card.type);
                }}
                sx={{
                  height: "100%",
                  width: "100%",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  position: "relative",
                  borderRadius: 4,
                  opacity: isDisabled ? 0.62 : 1,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": isDisabled
                    ? {}
                    : {
                        boxShadow: "0 16px 40px rgba(0,0,0,0.15)",
                        "& .icon-wrapper": {
                          transform: "scale(1.08)",
                          boxShadow: `0 8px 24px ${card.color}50`,
                        },
                        "& .card-badge": { transform: "translateX(0)", opacity: 1 },
                      },
                }}
              >
                {/* Persistent state chip — always visible, unlike the old
                    hover-only badge which meant the landlord had to mouse over
                    every card to learn what had already been uploaded. */}
                {style.chip && (
                  <Box sx={{ position: "absolute", top: 10, left: 12, zIndex: 1 }}>
                    <Chip
                      size="small"
                      label={style.chip.label}
                      sx={{
                        backgroundColor: style.chip.bg,
                        color: style.chip.fg,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        height: 22,
                      }}
                    />
                  </Box>
                )}

                {/* Hover badge */}
                {!isDisabled && (
                  <Box
                    className="card-badge"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 16,
                      transform: "translateX(100%)",
                      opacity: 0,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Chip
                      icon={
                        isTenantUploader ? (
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <StateIcon sx={{ fontSize: 16 }} />
                        )
                      }
                      label={
                        isTenantUploader
                          ? "View"
                          : status.state === "uploaded"
                          ? "Edit"
                          : status.state === "rejected"
                          ? "Re-upload"
                          : "Upload"
                      }
                      size="small"
                      sx={{
                        backgroundColor: card.color,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        height: 24,
                        "& .MuiChip-icon": { color: "#fff" },
                      }}
                    />
                  </Box>
                )}

                <CardContent sx={{ p: 3, pt: 4.5, textAlign: "center" }}>
                  <Box
                    className="icon-wrapper"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: card.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2.5,
                      fontSize: "2.5rem",
                      transition: "all 0.3s ease",
                      boxShadow: `0 4px 20px ${card.color}40`,
                      border: `3px solid ${card.lightColor}`,
                      filter: isDisabled ? "grayscale(0.7)" : "none",
                    }}
                  >
                    {card.icon}
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5, fontSize: "1.1rem" }}
                  >
                    {card.title}
                  </Typography>

                  {/* The amount is the thing the landlord actually wants to
                      confirm, so it replaces the static provider name once we
                      know it. */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: status.amount ? "#0f172a" : "#64748b",
                      fontSize: status.amount ? "1rem" : "0.875rem",
                      fontWeight: status.amount ? 700 : 500,
                    }}
                  >
                    {status.amount
                      ? `Rs ${Number(status.amount).toLocaleString()}`
                      : card.company}
                  </Typography>

                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${card.lightColor}` }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: style.color,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        fontSize: "0.75rem",
                        minHeight: 20,
                        lineHeight: 1.3,
                      }}
                    >
                      {isTenantUploader ? (
                        <>
                          <LockIcon sx={{ fontSize: 14 }} /> View Only
                        </>
                      ) : status.hint ? (
                        <>
                          <StateIcon sx={{ fontSize: 14 }} /> {status.hint}
                        </>
                      ) : AUTO_FILL_TYPES.includes(card.type) ? (
                        <>
                          <AutoFixHighIcon sx={{ fontSize: 14 }} /> Auto-Fill Available
                        </>
                      ) : null}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        );
      })}
    </Grid>
  );
}
